import type { CalendarEvent } from '../types';
import { calendarSharingStore } from '../stores/calendarSharingStore';
import { useSettingsStore } from '../stores/settingsStore';
import { BUSY_TITLE, PERSON_COLORS, placeActivity, typeColor, type ColorMode, type ShareLevel } from './sharing';

/** Il mio calendario resta del blu di sempre; i colleghi prendono gli altri colori in ordine alfabetico. */
export const MY_DEFAULT_COLOR = '#3B82F6';

export function colorForPerson(id: string, meId: string, orderedIds: readonly string[], custom?: Record<string, string> | null): string {
  const c = custom?.[id];
  if (c && /^#[0-9a-fA-F]{6}$/.test(c)) return c;
  if (id === meId) return MY_DEFAULT_COLOR;
  const others = orderedIds.filter(x => x !== meId);
  const i = Math.max(0, others.indexOf(id));
  return PERSON_COLORS[(i % (PERSON_COLORS.length - 1)) + 1];
}

export interface PresentContext {
  meId: string;
  visible: Readonly<Record<string, ShareLevel>>;
  shown: Readonly<Record<string, true>>;
  colorMode: ColorMode;
  orderedPeople: readonly string[];
  personColors?: Record<string, string> | null;
  typeColors?: Record<string, string> | null;
}

/**
 * Regola pura: dalle task commerciali caricate a quelle da mostrare, con livello, colore e pallino del tipo.
 * Chi vedo "solo occupato" diventa un blocco "Occupato" senza lead ne' testo, non apribile ne' spostabile.
 */
export function presentWith(events: readonly CalendarEvent[], ctx: PresentContext): CalendarEvent[] {
  const visible = new Map(Object.entries(ctx.visible));
  const shown = new Set(Object.keys(ctx.shown));
  const out: CalendarEvent[] = [];
  for (const ev of events) {
    const md = (ev.metadata || {}) as Record<string, any>;
    // Elemento senza proprietario (dato storico senza assegnatario né autore): resta visibile come prima
    if (!md.owner_id && !md.creator_id) { out.push(ev); continue; }
    // Quello che ho fissato io lo vedo sempre, almeno nei dettagli, come prima del calendario per persona:
    // anche se il calendario del collega e' spento, solo "occupato", non condiviso o il collega e' disattivato
    const ownerId = md.owner_id || md.creator_id;
    const placed = placeActivity({ assigned_to: md.owner_id, user_id: md.creator_id }, { meId: ctx.meId, visible, shown });
    const place = md.creator_id === ctx.meId
      ? { ownerId, level: ((placed?.level ?? visible.get(ownerId)) === 'edit' ? 'edit' : 'details') as ShareLevel }
      : placed;
    if (!place) continue;
    const pColor = colorForPerson(place.ownerId, ctx.meId, ctx.orderedPeople, ctx.personColors);
    const tColor = typeColor(md.activity_type, ctx.typeColors);
    const color = ctx.colorMode === 'type' ? tColor : pColor;
    const dot = ctx.colorMode === 'type' ? pColor : tColor;
    const done = !!md.is_completed;
    if (place.level === 'busy') {
      out.push({
        ...ev,
        title: BUSY_TITLE,
        description: '',
        color: '#9CA3AF',
        is_editable: false,
        metadata: { owner_id: place.ownerId, busy_only: true, activity_type: md.activity_type },
      });
      continue;
    }
    const canEdit = place.level === 'edit' || md.creator_id === ctx.meId;
    out.push({
      ...ev,
      color,
      is_editable: canEdit,
      metadata: { ...md, type_dot: dot, person_color: pColor, done, can_edit: canEdit, share_level: place.level },
    });
  }
  return out;
}

/**
 * Eventi del CRM, prenotazioni e task di progetto finiscono nel calendario della persona assegnata
 * (per gli eventi senza assegnatario, di chi li ha creati), con le stesse regole delle task commerciali.
 * `kind` fa da "tipo" per il pallino e il colore per tipo.
 */
export function withOwner(ev: CalendarEvent, ownerId: string | null | undefined, creatorId: string | null | undefined, kind: string): CalendarEvent {
  const md = (ev.metadata || {}) as Record<string, any>;
  return { ...ev, metadata: { ...md, owner_id: ownerId || creatorId || null, creator_id: creatorId || null, activity_type: md.activity_type || kind } };
}

/** Applica le regole con lo stato attuale (condivisioni, scelte personali, impostazioni dell'azienda). */
export function presentSalesActivities(events: CalendarEvent[]): CalendarEvent[] {
  const sh = calendarSharingStore.getState();
  if (!sh.ready || !sh.ctx) return events;
  const settings = useSettingsStore.getState().settings as any;
  const orgCal = settings?.calendar_settings || {};
  return presentWith(events, {
    meId: sh.ctx.meId,
    visible: sh.visible,
    shown: sh.shown,
    colorMode: sh.prefs.color_mode || orgCal.color_mode || 'person',
    orderedPeople: sh.people.map(p => p.id),
    personColors: sh.prefs.person_colors,
    typeColors: orgCal.type_colors,
  });
}

/**
 * Eventi del CRM messi nel calendario di un collega con "Calendario di" (metadata.calendar_owner_id): stanno nel suo
 * calendario come le task commerciali (si vedono se il suo calendario è acceso, col suo colore; "solo occupato" diventa
 * un blocco grigio). Chi l'ha creato lo vede sempre. Gli altri eventi restano di tutta l'azienda.
 */
export function presentOwnedEvents(
  events: readonly CalendarEvent[],
  ctx: Pick<PresentContext, 'meId' | 'visible' | 'shown' | 'orderedPeople' | 'personColors'>,
): CalendarEvent[] {
  const out: CalendarEvent[] = [];
  for (const ev of events) {
    const owner = (ev.metadata as any)?.calendar_owner_id as string | undefined;
    if (!owner || owner === ctx.meId) { out.push(ev); continue; }
    const mine = ev.created_by === ctx.meId;
    const level = ctx.visible[owner];
    if (!mine && (!level || !ctx.shown[owner])) continue;
    if (!mine && level === 'busy') {
      out.push({ ...ev, title: BUSY_TITLE, description: '', color: '#9CA3AF', is_editable: false, metadata: { owner_id: owner, busy_only: true } as any });
      continue;
    }
    out.push({
      ...ev,
      color: colorForPerson(owner, ctx.meId, ctx.orderedPeople, ctx.personColors),
      ...(mine || level === 'edit' ? {} : { is_editable: false }),
    });
  }
  return out;
}
